"use client"
import React from "react"
import {
  useCallbackRef,
  useDedupedItems,
  useIntersected,
  useSynchronizedScroll,
} from "../../hooks"
import { classNames } from "../../utils/classNames"
import { FlexColumn, FlexColumnProps } from "../FlexColumn"
import { ScrollingPaginator } from "../ScrollingPaginator"
import { UseScrollingPaginationOptions } from "../ScrollingPaginator/useScrollingPagination"
import { TableSize } from "./constants"
import {
  TableHeader,
  TableHeaderProps,
  TableHeaderWrapper,
} from "./TableHeader"
import { TableProvider } from "./TableProvider"
import { TableRow } from "./TableRow"

export type TableRowRenderProps<T, C> = {
  index: number
  item: T
  context: C | undefined
}

export type TableProps<T, C = unknown> = {
  contentClassName?: string
  contentStyle?: React.CSSProperties
  items: T[]
  context?: C
  header?: React.ReactElement<TableHeaderProps>
  renderRow: React.ComponentType<TableRowRenderProps<T, C>>
  itemKey: (item: T | undefined, index: number) => string | number | undefined
  size?: TableSize
  headerBorderOnScroll?: boolean
  headerBorder?: boolean
  paginationError?: boolean
  dividers?: boolean
} & Partial<UseScrollingPaginationOptions> &
  Pick<FlexColumnProps, "className" | "id" | "style">

const TableBase = <T, C = unknown>({
  className,
  items,
  id,
  header,
  contentClassName,
  itemKey,
  renderRow: Row,
  hasNext,
  loadNext,
  isLoadingNext,
  context,
  size = "md",
  headerBorderOnScroll: headerBorderOnScrollProp = true,
  style,
  contentStyle,
  paginationError,
  dividers,
}: TableProps<T, C>) => {
  const [headerRef, setHeaderRef] = useCallbackRef<HTMLDivElement>()
  const [tableRef, setTableRef] = useCallbackRef<HTMLDivElement>()

  const dedupedItems = useDedupedItems(items, itemKey)

  const headerRefChild = headerRef.current
    ?.firstElementChild as HTMLDivElement | null

  useSynchronizedScroll(
    headerRef.current?.firstElementChild as HTMLDivElement | null,
    tableRef.current,
  )

  const rows = dedupedItems.map((item, index) => {
    return (
      <div
        className="grid"
        data-index={index}
        key={itemKey?.(item, index) ?? index}
      >
        <Row context={context} index={index} item={items[index]} />
      </div>
    )
  })

  const isScrolledDown = useIntersected(
    tableRef,
    { current: headerRefChild },
    tableRef,
  )
  const showBorder = (isScrolledDown && headerBorderOnScrollProp) || dividers

  return (
    <TableProvider dividers={dividers} size={size}>
      <FlexColumn
        aria-rowcount={items.length}
        className={classNames("w-full min-w-0", className)}
        id={id}
        role="table"
        style={style}
      >
        {header && (
          // Display contents to allow header to become sticky with parent.
          <TableHeaderWrapper ref={setHeaderRef} showBorder={showBorder}>
            {header}
          </TableHeaderWrapper>
        )}
        <div
          className={classNames(
            "flex w-full flex-1 flex-col overflow-x-auto",
            size === "xs" ? "gap-1.5" : "gap-1",
            dividers && "gap-0",
            contentClassName,
          )}
          ref={setTableRef}
          role="rowgroup"
          style={contentStyle}
        >
          {rows}

          {(hasNext || paginationError) && loadNext && (
            <ScrollingPaginator
              hasError={paginationError}
              hasNext={hasNext}
              isLoadingNext={Boolean(isLoadingNext)}
              loadNext={loadNext}
            />
          )}
        </div>
      </FlexColumn>
    </TableProvider>
  )
}

export const Table = Object.assign(TableBase, {
  Header: TableHeader,
  Row: TableRow,
})
